import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/layout/Layout';
import {
  Trophy, Star, CalendarClock, Medal,
  Loader2, DollarSign, ArrowUpDown, History, Repeat, Megaphone, Info, AlertTriangle
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { useError } from '../../../context/ErrorContext';
import PaginationControls from '../../../components/common/PaginationControls';
import ExportExcelButton from '../../../components/common/ExportExcelButton';
import ExportModal, { ExportField } from '../../../components/common/ExportModal';
import { clientsApi } from '../../../api/clientsApi';
import ClientContactInfo from '../../../components/common/ClientContactInfo';
import SearchInput from '../../../components/common/SearchInput';
import { formatPhoneNumber } from '../../../utils/phoneUtils';
import { formatBirthday, formatDate } from '../../../utils/dates';
import { RUTA_CAMPANAS, prefillMejoresClientes, prefillReactivacion } from '../../analytics/utils/campaignActions';

// --- INTERFACES ---
interface WhatsAppClient {
  id: string;
  phoneNumber: string;
  visitorName: string | null;
  email: string | null;
  points: number;
  visitCount: number | null;
  tags: string[];
  createdAt: Date | string | null;
  lastVisit: Date | null | string;
  countryCode: string;
  gender: string;
  businessId: string;
  birthDay: number | null;
  birthMonth: number | null;
  registrationBranchId?: string;
  registrationBranchName?: string;
}

const ITEMS_PER_PAGE = 20;

// --- HELPERS ---
// O6: días transcurridos desde la última visita (recencia / riesgo de fuga)
const getDaysSince = (dateInput: Date | string | null): number | null => {
  if (!dateInput) return null;
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(date.getTime())) return null;
  return Math.floor((Date.now() - date.getTime()) / 86400000);
};

// O6: chip de recencia. Verde/muted si vino hace poco; ámbar a 30+ días; rojo "En fuga" a 60+.
const RecencyBadge: React.FC<{ lastVisit: Date | string | null }> = ({ lastVisit }) => {
  const days = getDaysSince(lastVisit);
  if (days === null) {
    return <span className="text-xs text-content-3 italic">Sin visitas registradas</span>;
  }
  const label = days === 0 ? 'hoy' : days === 1 ? 'ayer' : `hace ${days} días`;
  if (days >= 60) {
    return <span className="inline-flex items-center text-xs font-semibold text-red-700 dark:text-red-300"><AlertTriangle className="h-3 w-3 mr-1" /> En fuga · {label}</span>;
  }
  if (days >= 30) {
    return <span className="inline-flex items-center text-xs font-medium text-amber-700 dark:text-amber-300"><AlertTriangle className="h-3 w-3 mr-1" /> Sin venir {label}</span>;
  }
  return <span className="text-xs text-content-3">{label}</span>;
};

/** O8: formatea un saldo como moneda (separador de miles + 2 decimales) en modo cashback */
const formatMoney = (n: number): string =>
  (n ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Formatea género para exportación */
const formatGender = (gender: string | null | undefined): string => {
  if (!gender) return '-';
  if (gender === 'male') return 'Masculino';
  if (gender === 'female') return 'Femenino';
  return gender;
};

// ========================================================================
// DEFINICIÓN DE CAMPOS EXPORTABLES
// ========================================================================

const buildExportFields = (pointsHeader: string): ExportField[] => [
  { key: 'ranking',   header: 'Ranking',              label: 'Ranking',              alwaysIncluded: true,  columnWidth: 8 },
  { key: 'name',      header: 'Cliente',              label: 'Nombre del cliente',   alwaysIncluded: true,  columnWidth: 30 },
  { key: 'phone',     header: 'Teléfono',             label: 'Teléfono',             alwaysIncluded: true,  columnWidth: 20 },
  { key: 'points',    header: pointsHeader,           label: pointsHeader,           alwaysIncluded: true,  columnWidth: 15 },
  { key: 'visits',    header: 'Visitas',              label: 'Número de visitas',    defaultSelected: true, columnWidth: 10 },
  { key: 'lastVisit', header: 'Última Visita',        label: 'Última visita',        defaultSelected: true, columnWidth: 18 },
  { key: 'recency',   header: 'Días sin venir',       label: 'Días sin venir',       defaultSelected: true, columnWidth: 14 },
  { key: 'email',     header: 'Email',                label: 'Correo electrónico',   defaultSelected: true, columnWidth: 28 },
  { key: 'birthday',  header: 'Cumpleaños',           label: 'Cumpleaños',           defaultSelected: true, columnWidth: 14 },
  { key: 'gender',    header: 'Género',               label: 'Género',               defaultSelected: true, columnWidth: 14 },
  { key: 'branch',    header: 'Sucursal de Registro', label: 'Sucursal de registro', defaultSelected: true, columnWidth: 22 },
  { key: 'tags',      header: 'Etiquetas',            label: 'Etiquetas',            defaultSelected: true, columnWidth: 20 },
];

/** Mapa de extracción: dado un key, retorna el valor de celda para un cliente */
const getFieldValue = (
  key: string,
  client: WhatsAppClient,
  loyaltyMode: string,
  index: number
): string | number => {
  switch (key) {
    case 'ranking':   return index + 1;
    case 'name':      return client.visitorName || 'Sin nombre';
    case 'phone':     return formatPhoneNumber(client.countryCode, client.phoneNumber);
    case 'points':    return loyaltyMode === 'cashback'
                          ? (client.points ?? 0).toFixed(2)
                          : Math.floor(client.points ?? 0).toString();
    case 'visits':    return client.visitCount ?? '-';
    case 'lastVisit': return formatDate(client.lastVisit);
    case 'recency':   return getDaysSince(client.lastVisit) ?? '-';
    case 'email':     return client.email || '-';
    case 'birthday':  return formatBirthday(client.birthDay, client.birthMonth != null ? client.birthMonth - 1 : null); // 1-12 → 0-11
    case 'gender':    return formatGender(client.gender);
    case 'branch':    return client.registrationBranchName || '-';
    case 'tags':      return (client.tags || []).join(', ') || '-';
    default:          return '-';
  }
};

// --- MAIN COMPONENT ---

const BestClientsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { addError, clearErrors } = useError();

  const contentTopRef = useRef<HTMLDivElement>(null);

  // --- State ---
  const [clients, setClients] = useState<WhatsAppClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageChanging, setPageChanging] = useState(false);
  const [currentPageNum, setCurrentPageNum] = useState(1);
  const [totalHits, setTotalHits] = useState(0);
  const [currentSearchTerm, setCurrentSearchTerm] = useState("");
  
  // Criterio de Ordenamiento ('points' = Saldo, 'lifetime' = Histórico, 'frequency' = Nº visitas)
  const [sortBy, setSortBy] = useState<'points' | 'lifetime' | 'frequency'>('points');

  // O5: ventana de tiempo. Solo aplica a histórico/frecuencia; en "Saldo Actual" se ignora
  // (es una foto sin dimensión temporal) → effectivePeriod fuerza 'all' en ese modo.
  const [period, setPeriod] = useState<'all' | '30d' | '90d' | '12m'>('all');
  const effectivePeriod: 'all' | '30d' | '90d' | '12m' = sortBy === 'points' ? 'all' : period;

  // Estado del modal de exportación
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // O3: acción "premiar al top N" → crea una campaña dirigida a esos clientes
  const [topN, setTopN] = useState(50);
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  // Filtrar la campaña a solo los del Top N que están en fuga (30+ días sin venir) → win-back
  const [onlyChurned, setOnlyChurned] = useState(false);

  const loyaltyMode = currentUser?.loyaltyMode || 'visits';

  // Explicación breve del criterio de orden activo (cambia con sortBy y período)
  const periodLabel = period === '30d' ? 'los últimos 30 días'
    : period === '90d' ? 'los últimos 90 días'
    : period === '12m' ? 'los últimos 12 meses' : null;
  const sortHelpText = sortBy === 'lifetime'
    ? `Valor Histórico: puntos acumulados ${periodLabel ? 'en ' + periodLabel : 'desde siempre'}. No baja aunque el cliente canjee.`
    : sortBy === 'frequency'
      ? `Frecuencia de Visitas: número de visitas ${periodLabel ? 'en ' + periodLabel : 'de toda la vida'}. Si das puntos fijos por visita, coincide con el Valor Histórico.`
      : (loyaltyMode === 'cashback'
          ? 'Saldo Actual: saldo disponible hoy. Baja a quien ya canjeó su saldo. (El período no aplica a este modo.)'
          : 'Saldo Actual: puntos disponibles hoy. Baja a quien ya canjeó sus puntos. (El período no aplica a este modo.)');

  // Campos de exportación (memoizados según loyaltyMode y sortBy)
  const exportFields = React.useMemo(() => {
    const pointsLabel = sortBy !== 'points'
      ? 'Valor Histórico Total'
      : (loyaltyMode === 'cashback' ? 'Saldo Actual ($)' : 'Puntos Actuales');
    return buildExportFields(pointsLabel);
  }, [loyaltyMode, sortBy]);

  // --- Fetch Data ---
  const fetchBestClients = useCallback(async (
    term: string = "",
    page: number = 1,
    sortMode: 'points' | 'lifetime' | 'frequency' = 'points',
    periodMode: 'all' | '30d' | '90d' | '12m' = 'all'
  ) => {
    if (!currentUser?.businessId) {
      setClients([]); setTotalHits(0); setLoading(false); return;
    }

    const isPageChange = currentPageNum !== page;
    if (isPageChange) setPageChanging(true);
    else setLoading(true);

    setCurrentSearchTerm(term);
    if (clearErrors) clearErrors();

    try {
      const { clients: mappedClients, total } = await clientsApi.bestClients(
        { term, page, size: ITEMS_PER_PAGE, sort: sortMode, period: periodMode }
      );

      setTotalHits(total);
      setCurrentPageNum(page);

      if (isPageChange) {
        setTimeout(() => {
          setClients(mappedClients);
          setPageChanging(false);
        }, 200);
      } else {
        setClients(mappedClients);
        setLoading(false);
      }
    } catch (error: any) {
      console.error("Error fetching clients:", error);
      if (addError) addError(`Error al obtener clientes: ${error.message}`, undefined, "esFetchBest");
      setClients([]);
      setTotalHits(0);
      setCurrentPageNum(1);
      setPageChanging(false);
      setLoading(false);
    }
  }, [addError, clearErrors, currentUser?.businessId, currentPageNum]);

  // Efecto para cargar datos cuando cambia el término, el orden o el período
  useEffect(() => {
    fetchBestClients(currentSearchTerm, 1, sortBy, effectivePeriod);
  }, [sortBy, period]);

  // El fetch al cambiar de página lo disparan handleNextPage/handlePrevPage
  // directamente (ver abajo). NO usar un efecto sobre currentPageNum con guard
  // `> 1`: al volver a la página 1 no disparaba el fetch y dejaba pageChanging
  // colgado (spinner infinito + botones bloqueados) mostrando datos obsoletos.

  useEffect(() => {
    if (contentTopRef.current) {
      contentTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentPageNum]);

  // --- Handlers ---
  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSortBy(e.target.value as 'points' | 'lifetime' | 'frequency');
      setCurrentPageNum(1);
  };

  const handleSearch = (term: string | null) => {
    const searchTerm = term || "";
    setCurrentPageNum(1);
    fetchBestClients(searchTerm, 1, sortBy, effectivePeriod);
  };

  const handleNextPage = useCallback(() => {
    if (loading || pageChanging) return;
    if (currentPageNum * ITEMS_PER_PAGE < totalHits) {
      // fetchBestClients gestiona pageChanging y setCurrentPageNum internamente
      fetchBestClients(currentSearchTerm, currentPageNum + 1, sortBy, effectivePeriod);
    }
  }, [loading, pageChanging, currentPageNum, totalHits, currentSearchTerm, sortBy, effectivePeriod, fetchBestClients]);

  const handlePrevPage = useCallback(() => {
    if (loading || pageChanging) return;
    if (currentPageNum > 1) {
      fetchBestClients(currentSearchTerm, currentPageNum - 1, sortBy, effectivePeriod);
    }
  }, [loading, pageChanging, currentPageNum, currentSearchTerm, sortBy, effectivePeriod, fetchBestClients]);

  // ✅ EXPORTACIÓN CON SELECCIÓN DE CAMPOS
  const exportToExcel = useCallback(async (selectedKeys: string[]) => {
    if (!currentUser?.businessId) return;

    setIsExporting(true);

    try {
      // B3: tope de export. Avisamos si el ranking excede el tope (antes truncaba a 1000 en silencio).
      const EXPORT_CAP = 5000;
      const limit = Math.min(EXPORT_CAP, totalHits || EXPORT_CAP);

      const { clients: allClients } = await clientsApi.bestClients(
        { term: currentSearchTerm, page: 1, size: limit, sort: sortBy, period: effectivePeriod }
      );

      if (allClients.length === 0) {
         if (addError) addError("No hay datos para exportar", "warning");
         return;
      }

      if (totalHits > EXPORT_CAP && addError) {
        addError(`El reporte incluye solo el Top ${EXPORT_CAP.toLocaleString('es-MX')} de ${totalHits.toLocaleString('es-MX')} clientes.`, "warning");
      }

      // Construir headers y anchos dinámicamente según campos seleccionados
      const selectedFields = exportFields.filter(f => selectedKeys.includes(f.key));
      const headers = selectedFields.map(f => f.header);
      const colWidths = selectedFields.map(f => ({ wch: f.columnWidth || 15 }));

      // Generar filas dinámicamente
      const data = allClients.map((client, index) =>
        selectedFields.map(f => getFieldValue(f.key, client as WhatsAppClient, loyaltyMode, index))
      );
      
      const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
      ws['!cols'] = colWidths;
      const wb = XLSX.utils.book_new(); 
      XLSX.utils.book_append_sheet(wb, ws, 'Mejores Clientes');
      const dateStr = new Date().toLocaleDateString('sv');
      const sortLabel = sortBy === 'lifetime' ? 'historico' : 'actual';
      XLSX.writeFile(wb, `top_clientes_${sortLabel}_${dateStr}.xlsx`);

    } catch (error: any) { 
      console.error("Export error:", error);
      if (addError) addError("Error al generar el reporte.", undefined, 'exportExcel', error); 
    } finally {
      setIsExporting(false);
      setShowExportModal(false);
    }
  }, [currentUser?.businessId, currentSearchTerm, totalHits, loyaltyMode, sortBy, effectivePeriod, exportFields, addError]);

  // O3: resuelve los IDs del top N del ranking actual (modo vigente, sin término) y
  // navega al form de campañas pre-cargado como campaña dirigida a ESOS clientes.
  const handleCreateCampaign = useCallback(async () => {
    if (!currentUser?.businessId || creatingCampaign) return;
    setCreatingCampaign(true);
    try {
      const { clients: topClients } = await clientsApi.bestClients(
        { term: "", page: 1, size: topN, sort: sortBy, period: effectivePeriod }
      );
      let candidates = topClients;

      // Si se pide "solo en fuga", nos quedamos con los del Top N sin venir hace 30+ días
      if (onlyChurned) {
        candidates = candidates.filter((c) => {
          const d = getDaysSince(c.lastVisit);
          return d !== null && d >= 30;
        });
      }

      const ids: string[] = candidates
        .map((c) => c.id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0);

      if (ids.length === 0) {
        if (addError) addError(
          onlyChurned
            ? `No hay clientes inactivos (30+ días) dentro del Top ${topN}.`
            : "No hay clientes para crear la campaña.",
          "warning"
        );
        return;
      }
      // En fuga → mensaje de reactivación ("te extrañamos"); si no → mensaje VIP de premio.
      const prefill = onlyChurned ? prefillReactivacion(ids) : prefillMejoresClientes(ids);
      navigate(RUTA_CAMPANAS, { state: { prefill } });
    } catch (error: any) {
      console.error("Error preparando campaña top N:", error);
      if (addError) addError(`No se pudo preparar la campaña: ${error.message}`, undefined, "createCampaignTopN");
    } finally {
      setCreatingCampaign(false);
    }
  }, [currentUser?.businessId, creatingCampaign, topN, sortBy, effectivePeriod, onlyChurned, navigate, addError]);

  return (
    <Layout>
      <div className="space-y-6 px-2 sm:px-0">
        <div ref={contentTopRef}></div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
              <Trophy className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-yellow-500" /> 
              Mejores Clientes
            </h1>
            <p className="mt-1 text-sm text-content-2">Ranking de lealtad de tus clientes.</p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              {/* SELECTOR DE ORDENAMIENTO */}
              <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      {sortBy === 'lifetime'
                        ? <History className="h-4 w-4 text-purple-500"/>
                        : sortBy === 'frequency'
                          ? <Repeat className="h-4 w-4 text-blue-500"/>
                          : <ArrowUpDown className="h-4 w-4 text-content-3"/>}
                  </div>
                  <select
                      value={sortBy}
                      onChange={handleSortChange}
                      className="block w-full pl-10 pr-10 py-2 text-base border-line focus:outline-none focus:ring-accent focus:border-blue-500 sm:text-sm rounded-md shadow-sm bg-surface hover:bg-surface-2 transition-colors cursor-pointer"
                  >
                      <option value="points">Por Saldo Actual</option>
                      <option value="lifetime">Por Valor Histórico</option>
                      <option value="frequency">Por Frecuencia de Visitas</option>
                  </select>
              </div>

              {/* O5: selector de período (solo aplica a histórico/frecuencia) */}
              <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <CalendarClock className={`h-4 w-4 ${sortBy === 'points' ? 'text-content-3 opacity-40' : 'text-content-3'}`} />
                  </div>
                  <select
                      value={period}
                      onChange={(e) => { setPeriod(e.target.value as 'all' | '30d' | '90d' | '12m'); setCurrentPageNum(1); }}
                      disabled={sortBy === 'points'}
                      title={sortBy === 'points' ? 'El período no aplica al Saldo Actual' : 'Ventana de tiempo del ranking'}
                      className="block w-full pl-10 pr-10 py-2 text-base border-line focus:outline-none focus:ring-accent focus:border-blue-500 sm:text-sm rounded-md shadow-sm bg-surface hover:bg-surface-2 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                      <option value="all">Todo el tiempo</option>
                      <option value="30d">Últimos 30 días</option>
                      <option value="90d">Últimos 90 días</option>
                      <option value="12m">Últimos 12 meses</option>
                  </select>
              </div>

              <ExportExcelButton
                onExport={() => setShowExportModal(true)} 
                disabled={clients.length === 0 || loading} 
                className="animate-fade-in-up"
              />
          </div>
        </div>

        {/* Content */}
        <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <div className="px-4 py-5 sm:p-6">
            
            <div className="mb-4">
              <SearchInput onSearch={handleSearch} placeholder="Buscar por nombre o teléfono" className="max-w-lg" />
            </div>

            {/* Explicación breve del criterio de orden activo */}
            <div className="mb-4 flex items-start gap-2 text-xs text-content-3">
              <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>{sortHelpText}</span>
            </div>

            {/* O3: acción — crear campaña dirigida al top N del ranking actual */}
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-purple-200 dark:border-purple-500/30 bg-purple-50 dark:bg-purple-500/10 px-4 py-3">
              <div className="flex items-start sm:items-center text-sm text-content-2 flex-1">
                <Megaphone className="h-4 w-4 mr-2 mt-0.5 sm:mt-0 text-purple-600 dark:text-purple-300 flex-shrink-0" />
                <span>
                  {onlyChurned
                    ? 'Recupera a tus mejores clientes inactivos: campaña de WhatsApp solo a los del Top que dejaron de venir (30+ días).'
                    : 'Premia a tus mejores clientes: crea una campaña de WhatsApp dirigida al top del ranking actual.'}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                <label className="flex items-center gap-1.5 text-sm text-content-2 cursor-pointer mr-1">
                  <input
                    type="checkbox"
                    checked={onlyChurned}
                    onChange={(e) => setOnlyChurned(e.target.checked)}
                    className="rounded border-line text-purple-600 focus:ring-purple-500"
                  />
                  Solo inactivos (30+ días)
                </label>
                <label htmlFor="topN" className="text-sm text-content-2">Top</label>
                <select
                  id="topN"
                  value={topN}
                  onChange={(e) => setTopN(Number(e.target.value))}
                  className="rounded-md border-line bg-surface py-1.5 pl-2 pr-7 text-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <button
                  onClick={handleCreateCampaign}
                  disabled={creatingCampaign || loading || totalHits === 0}
                  className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {creatingCampaign ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Megaphone className="h-4 w-4 mr-1.5" />}
                  Crear campaña
                </button>
              </div>
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-line">
                  <thead className="bg-surface-2">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Ranking</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Cliente</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Última Visita</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">
                          {sortBy === 'frequency' ? (
                              <span className="text-blue-700 dark:text-blue-300 flex items-center"><Repeat className="w-3 h-3 mr-1"/> Visitas</span>
                          ) : 'Visitas'}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">
                          {sortBy !== 'points' ? (
                              <span className="text-purple-700 dark:text-purple-300 flex items-center"><History className="w-3 h-3 mr-1"/> Valor Histórico</span>
                          ) : (
                              loyaltyMode === 'cashback' ? 'Saldo Actual' : 'Puntos Actuales'
                          )}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-surface divide-y divide-line">
                    {loading && clients.length === 0 ? (
                      <tr><td colSpan={5} className="px-6 py-12 text-center text-sm text-content-3">Cargando ranking...</td></tr>
                    ) : clients.length === 0 ? (
                      <tr><td colSpan={5} className="px-6 py-12 text-center text-sm text-content-3">No se encontraron clientes.</td></tr>
                    ) : (
                      clients.map((client, index) => {
                        const globalRank = (currentPageNum - 1) * ITEMS_PER_PAGE + index + 1;
                        
                        let rankIcon = null;
                        if (globalRank === 1) rankIcon = <Trophy className="h-5 w-5 text-yellow-500" />;
                        else if (globalRank === 2) rankIcon = <Trophy className="h-5 w-5 text-content-3" />;
                        else if (globalRank === 3) rankIcon = <Trophy className="h-5 w-5 text-orange-600 dark:text-orange-400" />;
                        else if (globalRank <= 5) rankIcon = <Medal className="h-5 w-5 text-indigo-500" />;
                        else rankIcon = <span className="text-content-3 font-bold text-sm w-5 text-center">#{globalRank}</span>;

                        return (
                          <tr key={client.id} className="hover:bg-surface-2 transition-colors cursor-pointer" onClick={() => navigate(`/dashboard/clients/${client.id}/history`)}>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center justify-center w-8 h-8 bg-surface-2 rounded-full border border-line">
                                    {rankIcon}
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <ClientContactInfo
                                    clientId={client.id}
                                    clientName={client.visitorName}
                                    phoneNumber={client.phoneNumber}
                                    countryCode={client.countryCode}
                                    nameAsLink={false}
                                />
                                {client.tags.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {client.tags.map(tag => (
                                      <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300">
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-content-3">
                                <div className="flex flex-col gap-1">
                                  <div className="flex items-center">
                                    <CalendarClock className="h-4 w-4 mr-2 text-content-3" />
                                    {formatDate(client.lastVisit)}
                                  </div>
                                  <RecencyBadge lastVisit={client.lastVisit} />
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${sortBy === 'frequency' ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300' : 'bg-surface-2 text-content-2'}`}>
                                  <Repeat className="h-3.5 w-3.5 mr-1"/>
                                  {client.visitCount ?? '—'}
                                </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${sortBy !== 'points' ? 'bg-purple-100 dark:bg-purple-500/20 text-purple-800 dark:text-purple-300' : (loyaltyMode === 'cashback' ? 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300' : 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-300')}`}>
                                  {loyaltyMode === 'cashback' ? <DollarSign className="h-4 w-4 mr-1"/> : <Star className="h-4 w-4 mr-1"/>}
                                  {loyaltyMode === 'cashback' ? formatMoney(client.points) : Math.floor(client.points).toLocaleString('es-MX')}
                                </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
            </div>

            {/* Mobile View */}
            <div className="md:hidden space-y-3">
                {clients.map((client, index) => {
                    const globalRank = (currentPageNum - 1) * ITEMS_PER_PAGE + index + 1;
                    return (
                      <div key={client.id} onClick={() => navigate(`/dashboard/clients/${client.id}/history`)} className="border rounded-lg p-4 flex items-center justify-between active:bg-surface-2">
                          <div className="flex items-center gap-3 overflow-hidden">
                              <div className="flex-shrink-0 font-bold text-content-3 text-sm w-6">#{globalRank}</div>
                              <div className="min-w-0">
                                  <p className="text-sm font-medium text-content truncate">{client.visitorName || 'Sin nombre'}</p>
                                  <p className="text-xs text-content-3">{client.phoneNumber}</p>
                                  {(() => {
                                    const d = getDaysSince(client.lastVisit);
                                    if (d !== null && d >= 30) {
                                      return <span className={`inline-flex items-center text-xs font-medium mt-0.5 ${d >= 60 ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'}`}><AlertTriangle className="h-3 w-3 mr-1" /> {d >= 60 ? 'En fuga' : 'Sin venir'} · {d}d</span>;
                                    }
                                    return null;
                                  })()}
                                  {client.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {client.tags.map(tag => (
                                        <span key={tag} className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300">{tag}</span>
                                      ))}
                                    </div>
                                  )}
                              </div>
                          </div>
                          <div className="flex-shrink-0 flex flex-col items-end gap-1">
                              <div className={`flex items-center ${sortBy !== 'points' ? 'text-purple-700 dark:text-purple-300' : (loyaltyMode === 'cashback' ? 'text-green-700 dark:text-green-300' : 'text-yellow-700 dark:text-yellow-300')} font-bold text-sm`}>
                                  {loyaltyMode === 'cashback' ? <DollarSign className="h-3 w-3 mr-0.5"/> : <Star className="h-3 w-3 mr-0.5"/>}
                                  {loyaltyMode === 'cashback' ? formatMoney(client.points) : Math.floor(client.points).toLocaleString('es-MX')}
                              </div>
                              <div className={`flex items-center text-xs ${sortBy === 'frequency' ? 'text-blue-600 dark:text-blue-300 font-semibold' : 'text-content-3'}`}>
                                  <Repeat className="h-3 w-3 mr-0.5"/>
                                  {client.visitCount ?? '—'} visitas
                              </div>
                          </div>
                      </div>
                    );
                })}
            </div>

            {/* Pagination */}
            {totalHits > ITEMS_PER_PAGE && (
              <PaginationControls
                currentPage={currentPageNum}
                isLoading={loading || pageChanging}
                onPrevPage={handlePrevPage}
                onNextPage={handleNextPage}
                canGoPrev={currentPageNum > 1 && !pageChanging}
                canGoNext={currentPageNum * ITEMS_PER_PAGE < totalHits && !pageChanging}
                itemsPerPage={ITEMS_PER_PAGE}
                countOnPage={clients.length}
                totalItems={totalHits}
              />
            )}
          </div>
        </div>
      </div>

      {pageChanging && (
        <div className="fixed bottom-6 right-6 bg-surface rounded-full shadow-xl p-3 flex items-center animate-fade-in z-50 border border-line">
          <Loader2 className="h-5 w-5 text-accent animate-spin mr-2" />
          <span className="text-xs font-medium text-content-2">Cargando...</span>
        </div>
      )}

      {/* Modal de selección de campos para exportar */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={exportToExcel}
        fields={exportFields}
        title="Exportar Mejores Clientes"
        description="Selecciona los campos que deseas incluir en el reporte Excel."
        totalRecords={totalHits}
        isExporting={isExporting}
      />
    </Layout>
  );
};

export default BestClientsPage;